import { TouchEventHandler, useCallback, useMemo, useRef } from 'react';

import { requestAnimation } from '@hh.ru/magritte-common-func-utils';
import {
    SwipeEvent,
    SwipeEventHandler,
    SwipeThreshold,
    UseSwipeHandlersType,
    UseSwipeType,
} from '@hh.ru/magritte-common-use-swipe/types';

const DECELERATION_RATE = 0.998;
const VELOCITY_SMOOTHING_FACTOR = 0.75;
export const MAIN_AXIS_THRESHOLD_PX = 5;

type SwipeState = {
    x: number;
    y: number;
    distanceX: number;
    distanceY: number;
    velocityX: number;
    velocityY: number;
    startX: number;
    startY: number;
    time: DOMHighResTimeStamp;
};

const checkSwipeThreshold = (
    startCoordinate: number,
    coordinate: number,
    prevCoordinate: number,
    threshold: SwipeThreshold
) => {
    return (
        (!Number.isNaN(threshold.minDelta) &&
            coordinate - startCoordinate < (threshold.minDelta as number) &&
            coordinate < prevCoordinate) ||
        (!Number.isNaN(threshold.maxDelta) &&
            coordinate - startCoordinate > (threshold.maxDelta as number) &&
            coordinate > prevCoordinate) ||
        (!Number.isNaN(threshold.min) && coordinate < (threshold.min as number) && coordinate < prevCoordinate) ||
        (!Number.isNaN(threshold.max) && coordinate > (threshold.max as number) && coordinate > prevCoordinate)
    );
};

const handleSwipeEvent = (
    event: Omit<SwipeEvent, 'preventDefault'>,
    eventHandler?: SwipeEventHandler,
    defaultCallback?: VoidFunction
) => {
    let _default = true;
    eventHandler?.({ ...event, preventDefault: () => (_default = false) });
    _default && defaultCallback?.();
};

/**
 * Определяет, что пользователь сделал свайп или предположительно хотел сделать свайп за счет инерции
 * до координат thresholdRef.current.min/thresholdRef.current.max по оси axis относительно вьюпорта.
 * Возвращает обработчики нативных touch-ивентов для DOM-элемента, свайп по которому нужно регистрировать.
 */
export const useSwipeHandlers: UseSwipeHandlersType = ({
    thresholdXRef,
    thresholdYRef,
    onSwipeStart,
    onSwipeMove,
    onSwipeEnd,
    onSwipeCancel,
}) => {
    const stateRef = useRef<SwipeState | null>(null);

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const handleSwipeStart = useCallback(
        requestAnimation((time, touchEvent: TouchEvent) => {
            if (touchEvent.changedTouches.length !== 1) {
                return;
            }

            const { pageX: x, pageY: y } = touchEvent.changedTouches[0];
            const swipeEvent = { x, y, deltaX: 0, deltaY: 0, distanceX: 0, distanceY: 0, velocityX: 0, velocityY: 0 };
            handleSwipeEvent(swipeEvent, onSwipeStart, () => {
                stateRef.current = { ...swipeEvent, startX: x, startY: y, time };
            });
        }),
        [onSwipeStart]
    );

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const handleSwipeMove = useCallback(
        requestAnimation((time, touchEvent: TouchEvent) => {
            if (touchEvent.changedTouches.length !== 1 || !stateRef.current) {
                return;
            }

            const { pageX: x, pageY: y } = touchEvent.changedTouches[0];
            const prevState = stateRef.current;
            const deltaX = x - prevState.x;
            const deltaY = y - prevState.y;
            const distanceX = prevState.distanceX + deltaX;
            const distanceY = prevState.distanceY + deltaY;

            // скорость (velocity) свайпа, расчитанная по двум последним точкам, подвержена влиянию случайного шума,
            // поэтому мы применяем https://en.wikipedia.org/wiki/Exponential_smoothing для сглаживания
            const _velocityX = (deltaX * 1000) / (time - prevState.time);
            const velocityX =
                prevState.velocityX * VELOCITY_SMOOTHING_FACTOR + _velocityX * (1 - VELOCITY_SMOOTHING_FACTOR);
            const _velocityY = (deltaY * 1000) / (time - prevState.time);
            const velocityY =
                prevState.velocityY * VELOCITY_SMOOTHING_FACTOR + _velocityY * (1 - VELOCITY_SMOOTHING_FACTOR);

            const swipeEvent = { x, y, deltaX, deltaY, distanceX, distanceY, velocityX, velocityY };
            handleSwipeEvent(swipeEvent, onSwipeMove, () => {
                stateRef.current = { ...prevState, ...swipeEvent, distanceX, distanceY, time };
            });
        }),
        [onSwipeMove]
    );

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const handleSwipeEnd = useCallback(
        requestAnimation((_, touchEvent: TouchEvent) => {
            if (
                touchEvent.changedTouches.length !== 1 ||
                !stateRef.current ||
                (!thresholdXRef?.current && !thresholdYRef?.current)
            ) {
                return;
            }

            // если координата касания не достигла thresholdRef.current.min/thresholdRef.current.max,
            // считаем проекцию (projection) — предполагаемую координату, до которой должен дойти свайп по инерции
            // на основе последней координаты касания, скорости (velocity) свайпа и замедления инерции (DECELERATION_RATE)
            //
            // регистрируем свайп, если проекция достигла thresholdRef.current.min/thresholdRef.current.max
            //
            // формула расчета проекции взята из презентации Apple https://developer.apple.com/videos/play/wwdc2018/803/
            // подробнее https://medium.com/ios-os-x-development/gestures-in-fluid-interfaces-on-intent-and-projection-36d158db7395
            const deltaX = ((stateRef.current.velocityX / 1000) * DECELERATION_RATE) / (1 - DECELERATION_RATE);
            const deltaY = ((stateRef.current.velocityY / 1000) * DECELERATION_RATE) / (1 - DECELERATION_RATE);
            const projectionX = stateRef.current.x + deltaX;
            const projectionY = stateRef.current.y + deltaY;
            const projectionDistanceX = stateRef.current.distanceX + deltaX;
            const projectionDistanceY = stateRef.current.distanceY + deltaY;
            const swipeEvent = {
                x: stateRef.current.x,
                y: stateRef.current.y,
                deltaX,
                deltaY,
                distanceX: stateRef.current.distanceX,
                distanceY: stateRef.current.distanceY,
                projectionDistanceX,
                projectionDistanceY,
                velocityX: stateRef.current.velocityX,
                velocityY: stateRef.current.velocityY,
            };

            const checkXThreshold =
                thresholdXRef?.current &&
                Math.abs(stateRef.current.distanceX) > MAIN_AXIS_THRESHOLD_PX &&
                Math.abs(stateRef.current.distanceX) > Math.abs(stateRef.current.distanceY) &&
                checkSwipeThreshold(stateRef.current.startX, projectionX, stateRef.current.x, thresholdXRef.current);
            const checkYThreshold =
                thresholdYRef?.current &&
                Math.abs(stateRef.current.distanceY) > MAIN_AXIS_THRESHOLD_PX &&
                Math.abs(stateRef.current.distanceY) > Math.abs(stateRef.current.distanceX) &&
                checkSwipeThreshold(stateRef.current.startY, projectionY, stateRef.current.y, thresholdYRef.current);
            if (checkXThreshold || checkYThreshold) {
                handleSwipeEvent(swipeEvent, onSwipeEnd, () => (stateRef.current = null));
            } else {
                handleSwipeEvent(swipeEvent, onSwipeCancel, () => (stateRef.current = null));
            }
        }),
        [onSwipeCancel, onSwipeEnd]
    );

    return useMemo(() => {
        return {
            onTouchStart: handleSwipeStart,
            onTouchMove: handleSwipeMove,
            onTouchEnd: handleSwipeEnd,
            onTouchCancel: handleSwipeEnd,
        };
    }, [handleSwipeStart, handleSwipeMove, handleSwipeEnd]);
};

/**
 * Определяет, что пользователь сделал свайп или предположительно хотел сделать свайп за счет инерции
 * до координат thresholdRef.current.min/thresholdRef.current.max по оси axis относительно вьюпорта.
 * Возвращает обработчики React touch-ивентов для DOM-элемента, свайп по которому нужно регистрировать.
 */
export const useSwipe: UseSwipeType = (params) => {
    const swipeHandlers = useSwipeHandlers(params);

    return useMemo(() => {
        const onTouchStart: TouchEventHandler = (event) => swipeHandlers.onTouchStart(event.nativeEvent);
        const onTouchMove: TouchEventHandler = (event) => swipeHandlers.onTouchMove(event.nativeEvent);
        const onTouchEnd: TouchEventHandler = (event) => swipeHandlers.onTouchEnd(event.nativeEvent);
        const onTouchCancel: TouchEventHandler = (event) => swipeHandlers.onTouchCancel(event.nativeEvent);
        return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel };
    }, [swipeHandlers]);
};
